import { Component, EventEmitter, inject, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { IonicModule, Platform } from '@ionic/angular';
import { SpeechRecognition } from "@capacitor-community/speech-recognition";

@Component({
  selector: 'app-textarea-with-counter',
  standalone: true,
  imports: [MatFormFieldModule,IonicModule, MatInputModule, ReactiveFormsModule, CommonModule, FormlyModule],
  template: `
        <mat-form-field [appearance]="props.appearance" class="full-width mic-wrapper">
          <mat-label>{{ props.label }}</mat-label>

          <textarea
            matInput
            [formControl]="formControl"
            [formlyAttributes]="field"
            [placeholder]="props.placeholder"
            [rows]="props.rows || 3"
            [maxlength]="props.maxLength || 300"
            [required]="props.required"
            (input)="onInputChange($event)"
            (focus)="onFocus($event)"
          ></textarea>

          <!-- MIC ICON OVERLAY -->
          <div
            class="mic-icon"
            [class.recording]="isRecording"
            (click)="toggleRecording()"
          >
            <ion-icon [name]="isRecording ? 'mic-off-outline' : 'mic-outline'"
            ></ion-icon>
          </div>

          <mat-hint align="end" [class.error-text]="isOverLimit">
            {{ currentLength }}/{{ props.maxLength || 300 }}
          </mat-hint>

          <mat-error *ngIf="showError">
            {{ validationMessage }}
          </mat-error>
        </mat-form-field>

  `,
  styles: [`
      ::ng-deep .mat-mdc-form-field-infix {
        padding-right: 10px !important;
      }
      .mic-wrapper {
        position: relative;
      }

      .mic-icon {
        position: absolute;
        top: -7px;
        right: -23px;
        width: 38px;
        height: 38px;
        background: #ffffff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        cursor: pointer;
        z-index: 10;
        transition: all 0.25s ease-in-out;
      }

      .mic-icon ion-icon {
        font-size: 22px;
        color: #007382;
        transition: color 0.25s ease-in-out;
      }

     
      .mic-icon.recording {
        background: #fff;
        box-shadow: 0 0 0 8px rgba(255,0,0,0.25);
        animation: pulse 1.4s infinite ease-in-out;
      }

      .mic-icon.recording ion-icon {
        color:#007382;
      }

      /* ❤️ Pulse Animation */
      @keyframes pulse {
        0% {
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.3);
        }
        70% {
          transform: scale(1.12);
          box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
        }
        100% {
          transform: scale(1);
        }
      }



    .full-width {
      width: 100%;
      padding:10px 3px 0 0;
    }
    .error-text {
      color: #f44336;
    }
    mat-hint {
      font-size: 12px;
    }
  `]
})
export class TextareaWithCounterComponent extends FieldType<FieldTypeConfig> implements OnInit {
  currentLength = 0;
  isOverLimit = false;
  validationMessage = '';
  private focusHandled = false; // Prevent double focus on mobile
  description: WritableSignal<string> = signal("");
  isRecording = false;
  platform = inject(Platform);
  isPlatformWeb: WritableSignal<boolean> = signal(true);
  @Input() value: string = "";
  @Input() maxLength: number = 250;
  @Input() isReadOnly: boolean = false;
  @Output() hasError: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>();
  @Input() set clickedOutside(value: boolean) {
    if (value) {
      this.StopRecordingSpeechToText();
    }
  }
  @Output() clickedOutsideChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  ngOnInit() {
    this.updateCounter();
    this.description.set("");
    this.StopRecordingSpeechToText();
    if (
      this.platform.is("ios") ||
      this.platform.is("tablet") ||
      this.platform.is("android")
    ) {
      this.RequestPermission();
      this.isPlatformWeb.set(false);
    }
    this.isPlatformWeb.set(false);
  }
  private async RequestPermission() {
    const permission = await SpeechRecognition.requestPermissions();
    if (permission.speechRecognition === "denied") {
      await SpeechRecognition.requestPermissions();
    }
  }

  onFocus(event: any) {
    // Prevent multiple focus events on mobile devices
    if (this.focusHandled) return;

    this.focusHandled = true;

    // Reset flag after a delay to allow legitimate refocus
    setTimeout(() => {
      this.focusHandled = false;
    }, 300);
  }

  updateCounter() {
    const value = this.formControl.value || '';
    this.currentLength = value.length;
    this.isOverLimit = this.currentLength > (this.props.maxLength || 300);

    if (this.isOverLimit) {
      this.validationMessage = 'Maximum 300 characters allowed.';
    }
  }

  get showError(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }
  async StopRecordingSpeechToText() {
    this.value = this.value + this.description();
    this.valueChange.emit(this.value);

    // this.isRecording.next(false);
    await SpeechRecognition.stop();
    await SpeechRecognition.removeAllListeners();
    this.description.set("");
  }
  onInputChange(event: Event): void {
    this.updateCounter();
    this.StopRecordingSpeechToText();
    if (
      event.target &&
      (event.target as HTMLInputElement).value.length > this.maxLength
    ) {
      (event.target as HTMLInputElement).value = (
        event.target as HTMLInputElement
      ).value.slice(0, this.maxLength);
    }
    const inputValue = (event.target as HTMLTextAreaElement).value;

    this.valueChange.emit(inputValue);

    this.value = inputValue;
    this.valueChange.emit(this.value);
    this.hasError.emit(this.value.length >= this.maxLength);
  }
  async StartRecordingSpeechToText() {
    this.clickedOutside = false;
    this.clickedOutsideChange.emit(this.clickedOutside);
    const speechRecognition = await SpeechRecognition.available();

    if (speechRecognition.available) {
      // this.isRecording.next(true);
      await SpeechRecognition.start({
        maxResults: 1,
        language: "en-US",
        partialResults: true,
      }).catch((error) => {
        console.log("Error:", error);
      });

      await SpeechRecognition.removeAllListeners();
      SpeechRecognition.addListener(
        "partialResults",
        (data: { matches: string[] }) => {
          if (data.matches.length && data.matches[0] !== "") {
            const latestPartial = data.matches[data.matches.length - 1].trim();
            console.log("latestPartial :::", latestPartial);
      
            const existing = (this.formControl.value || "").trim();
      
            // If existing is empty, just set the partial text
            if (!existing) {
              this.formControl.setValue(latestPartial);
              return;
            }
      
            // Avoid duplicating word sequences
            const existingWords = existing.split(" ");
            const partialWords = latestPartial.split(" ");
      
            // If partial already starts with the last existing word(s), avoid duplicates
            let cleanedPartial = latestPartial;
      
            // Check how many words overlap from end
            for (let i = partialWords.length; i > 0; i--) {
              const partialSlice = partialWords.slice(0, i).join(" ");
              const existingSlice = existingWords.slice(-i).join(" ");
      
              if (partialSlice === existingSlice) {
                // Remove the overlapping words
                cleanedPartial = partialWords.slice(i).join(" ");
                break;
              }
            }
      
            const appended = (existing + " " + cleanedPartial).trim();
      
            this.formControl.setValue(appended);
          }
        }
      );
      

      SpeechRecognition.addListener(
        "listeningState",
        (listening: { status: "started" | "stopped" }) => {
          if (listening.status === "stopped") {
            this.isRecording=false;
            // console.log("isRecording ::",this.isRecording.getValue());
            console.log("Listening stopped:", listening.status);
            this.StopRecordingSpeechToText();
          }
        },
      ).catch((error) => {
        console.log("Listening State Error:", error);
      });
    }
  }
  toggleRecording() {
    this.isRecording = !this.isRecording;
  
    if (this.isRecording) {
      this.StartRecordingSpeechToText();
    } else {
      this.StopRecordingSpeechToText();
    }
  }
}
